// * First Actor View or Individual Actor View

import { VALUE_CHAIN_ACTOR_ICONS } from "../config";

const farmer = {
  sectionA: {
    icon: VALUE_CHAIN_ACTOR_ICONS.farmer,
    title: "Farmer",
  },
  sectionB: {
    list: [
      {
        text: "The following points are important to consider when thinking about smallholders/ farmers in the supply chain:",
      },
      {
        list: [
          {
            text: "Smallholders and farmers may not actually know or understand which products or where their crops are finally used across the world",
          },
          {
            text: "Smallholders and farmers are often driven by immediate need of cash flow. Their risk is far greater than the risk of any other downstream value chain actor",
          },
          {
            text: "Smallholder and farmers are typically the most vulnerable actors in a supply chain and often have to operate on a high trust basis. They will want to prioritise selling to those who are consistent, reliable and fair in their transactions, but often have little scope for choice",
          },
        ],
      },
    ],
  },
  sectionC: {
    list: [
      "Integrating sustainable procurement practices throughout sourcing strategies can enable you to consider the opportunities to create value for both your business and smallholders and farmers.",
      "IDH has created a guide for how to integrate sustainability throughout the process of creating or updating a sourcing strategy, and suggested practices to explore and use.",
    ],
  },
  sectionD: {
    title: "Where you can start",
    list: [
      "Every company should use the sourcing strategy approach to integrate the sustainable processes that bring the most value to them individually. However, as a starting point, here are three practices that would be applicable to many businesses and help to deliver value for smallholders and farmers too:",
    ],
  },
};

const primary = {
  sectionA: {
    icon: VALUE_CHAIN_ACTOR_ICONS.primary,
    title: "Primary/Secondary Processor",
  },
  sectionB: {
    list: [
      {
        text: "To get started, it may help to consider the following questions:",
      },
      {
        list: [
          {
            text: "Are you facing security of supply issues linked to weather impacts or low-quality products?",
          },
          {
            text: "Are you facing challenges meeting customer sustainability demands and maintaining viable margins?",
          },
          {
            text: "Are you getting customer demand for sustainably or responsibly procured materials?",
          },
        ],
      },
      {
        text: "Any materials or commodities for which the answer to any of these questions is yes is where you should start with sustainable procurement.",
      },
    ],
  },
  sectionC: {
    list: [
      "Sustainable procurement should be integrated thoughtfully into your purchasing approach for key spend categories. This means adopting practices into your sourcing strategies.",
      "IDH has created a guide for how to integrate sustainability throughout the process of creating or updating a sourcing strategy, and suggested practices to explore and use.",
    ],
  },
  sectionD: {
    title: "Where you can start",
    list: [
      "Using the sourcing strategy approach to integrate the sustainable processes will allow you to identify the practices that will bring most value to your individual business. However, as a starting point, here are three practices that are likely useful to all primary/secondary processors:",
    ],
  },
};

const trader = {
  sectionA: {
    icon: VALUE_CHAIN_ACTOR_ICONS.trader,
    title: "Trader",
  },
  sectionB: {
    list: [
      {
        text: "To get started, it may help to consider the following questions:",
      },
      {
        list: [
          {
            text: "Are you getting customer demand for sustainably or responsibly procured materials?",
          },
          {
            text: "Are you facing challenges meeting customer sustainability demands and maintaining viable margins?",
          },
          {
            text: "Is sustainability legislation limiting your ability to import or export certain goods from certain markets?",
          },
        ],
      },
      {
        text: "Any products or commodities for which the answer to any of these questions is yes is where you should start with sustainable procurement.",
      },
    ],
  },
  sectionC: {
    list: [
      "Sustainable procurement should be integrated thoughtfully into your purchasing approach for key spend categories. This means adopting practices into your sourcing strategies.",
      "IDH has created a guide for how to integrate sustainability throughout the process of creating or updating a sourcing strategy, and suggested practices to explore and use.",
    ],
  },
  sectionD: {
    title: "Where you can start",
    list: [
      "Using the sourcing strategy approach to integrate the sustainable processes will allow you to identify the practices that will bring most value to your individual business. However, as a starting point, here are three practices that are likely useful to all traders:",
    ],
  },
};

const manufacturer = {
  sectionA: {
    icon: VALUE_CHAIN_ACTOR_ICONS.manufacturer,
    title: "Manufacturer/Processor",
  },
  sectionB: {
    list: [
      {
        text: "To get started, it may help to consider the following questions:",
      },
      {
        list: [
          {
            text: "Is the spend category business critical that I am buying for?",
          },
          {
            text: "Are there sustainability commitments in the business that are impacted by the sourcing of the spend category?",
          },
          {
            text: "Is there legislation driving sustainability requirements for any spend categories?",
          },
          {
            text: "Is your business or brand reputation at stake through sourcing of this spend category? i.e. consumer, investor or NGO pressure",
          },
        ],
      },
      {
        text: "Any spend categories for which the answer to any of these questions is yes is where you should start with sustainable procurement.",
      },
    ],
  },
  sectionC: {
    list: [
      "Sustainable procurement should be integrated thoughtfully into your purchasing approach for key spend categories. This means adopting practices into your sourcing strategies.",
      "IDH has created a guide for how to integrate sustainability throughout the process of creating or updating a sourcing strategy, and suggested practices to explore and use.",
    ],
  },
  sectionD: {
    title: "Where you can start",
    list: [
      "Using the sourcing strategy approach to integrate the sustainable processes will allow you to identify the practices that will bring most value to your individual business. However, as a starting point, here are three practices that are likely useful to all manufacturers/processors:",
    ],
  },
};

const retailer = {
  sectionA: {
    icon: VALUE_CHAIN_ACTOR_ICONS.manufacturer,
    title: "Retailer",
  },
  sectionB: {
    list: [
      {
        text: "To get started, it may help to consider the following questions:",
      },
      {
        list: [
          {
            text: "How business critical is each retail category for growth?",
          },
          {
            text: "Does the business have sustainability commitments that are impacted by the sourcing of the retail category?",
          },
          {
            text: "Is there legislation driving sustainability requirements for any retail categories?",
          },
          {
            text: "Is your business or brand reputation at stake through sourcing of this retail category? i.e. consumer, investor or NGO pressure",
          },
          {
            text: "Are you facing consumer pressure on costs, but know that the least-cost options expose you to reputational or legislation risk?",
          },
        ],
      },
      {
        text: "Any retail categories for which the answer to any of these questions is yes is where you should start with sustainable procurement.",
      },
    ],
  },
  sectionC: {
    list: [
      "Sustainable procurement should be integrated thoughtfully into your purchasing approach for key spend categories. This means adopting practices into your sourcing strategies.",
      "IDH has created a guide for how to integrate sustainability throughout the process of creating or updating a sourcing strategy, and suggested practices to explore and use.",
    ],
  },
  sectionD: {
    title: "Where you can start",
    list: [
      "Using the sourcing strategy approach to integrate the sustainable processes will allow you to identify the practices that will bring most value to your individual business. However, as a starting point, here are three practices that are likely useful to all retailers:",
    ],
  },
};

export const FIRST_ACTOR_CONTENT = {
  farmer,
  primary,
  trader,
  manufacturer,
  retailer,
};
